import { Box, Button } from "@material-ui/core";
import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  IFlightListData,
  FareclassOptionFilter,
  AlgomerchTag,
  mapAlgomerchTexts,
  FiatPrice,
  FlightShopStep,
  TripDetails,
  MODAL_ALERT,
  ModalScreens,
  ModalCategoryType,
  SELECTED_FLIGHT,
  VIEWED_SLICE,
} from "redmond";
import { NoResults, useDeviceTypes } from "halifax";
import clsx from "clsx";
import { VIEWED_FLIGHT_LIST } from "redmond/build/apis/tysons";
import { IFlightListProps } from "./container";
import { ClientContext } from "../../../../App";
import {
  INITIAL_RESULT_SET_SIZE,
  SHOW_MORE_NUM,
} from "./components/FlightListInfo/textConstants";
import { FlightAlgomerchModal } from "../FlightAlgomerchModal";
import { ITripDetailsByTripId } from "../../reducer";
import {
  goToBook,
  goToReview,
} from "../../../hotel-shop/utils/queryStringHelpers";
import * as constants from "../../../book/actions/constants";
import { trackEvent } from "../../../../api/v0/trackEvent";
import {
  CLEAR_FILTERS_CTA_TEXT,
  FLIGHTS_NOT_FOUND_SUBTITLE,
  FLIGHTS_NOT_FOUND_TITLE,
} from "./constants";
import { DesktopFlightList } from "./components/DesktopFlightList";
import { MobileFlightList } from "./components/MobileFlightList";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { useQueryParamState } from "@capone/common";

export enum ModalTypes {
  AlgomerchModal,
}

export const getSelectCtaCopy = (
  input: string,
  usePriceText?: boolean,
  isMobile?: boolean,
) =>
  usePriceText ? (
    <>
      {!isMobile ? "Continue for " : ""}
      <strong>{input} per traveler</strong>
    </>
  ) : (
    `Select ${input}`
  );

export type IOpenModal = ModalTypes | false;

export const getSliceFareDetails = (args: {
  tripDetailsById: ITripDetailsByTripId;
  fareTrips: any;
}): TripDetails | null => {
  const { tripDetailsById, fareTrips } = args;
  if (!fareTrips.length) return null;
  let sliceDetails: TripDetails | null = null;
  sliceDetails = { ...tripDetailsById[fareTrips[0].trip] } as TripDetails;
  sliceDetails.fareDetails = [];
  fareTrips.forEach((fareTrip: any) => {
    const fareDetailByFareId = tripDetailsById[fareTrip.trip].fareDetails.find(
      (fareDetail) => fareDetail.id === fareTrip.fare,
    );
    if (fareDetailByFareId && sliceDetails)
      sliceDetails.fareDetails.push(fareDetailByFareId);
  });
  sliceDetails.fareDetails.sort(
    (fare1, fare2) =>
      (fare1?.paxPricings?.[0].pricing.baseAmount.fiat.value || 0) -
      (fare2?.paxPricings?.[0].pricing.baseAmount.fiat.value || 0),
  );

  return sliceDetails as TripDetails;
};

export const FlightList = (props: IFlightListProps) => {
  const {
    flights,
    rewardsKey,
    packageNightCountBasedOnAvailability,
    packageNightCountBasedOnOutboundFlight,
    packagesByOutboundFareSlice,
    packagesByReturnFareSlice,
    isFlightShopLoading,
    isTripDetailsLoading,
    tripDetailsById,
    fetchPackagesTripDetails,
    setPackagesChosenOutgoingSlice,
    setPackagesChosenReturnSlice,
    isOutgoing,
    isReturn,
    history,
    sortedFlights,
    maxPriceFilter,
    outgoingRecommendedFlight,
    returnRecommendedFlight,
    hasFlightShopCallFailed,
    isFlightShopCallNotCalled,
    departureDate,
    returnDate,
    airports,
    hasAppliedNonFareclassFilter,
    resetFilters,
    viewedFlightListProperties,
    selectedOutgoingSliceProperties,
    selectedReturnSliceProperties,
    hotelShopDateRange,
    setHotelShopDateRange,
    selectedRewardsAccount,
    canEarnRewards,
  } = props;
  const { matchesMobile } = useDeviceTypes();
  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;

  const [faresToShow, setFaresToShow] = useState<any[]>([]);
  const [expandedFlight, setExpandedFlight] = useQueryParamState(
    history,
    "expandedFlight",
  );
  const [selectedFareId, setClickedFareId] = useState("");
  const [fareTrips, setFareTrips] = useState([]);

  const [openModal, setOpenModal] = React.useState<IOpenModal>(false);
  const [selectedAlgomerchTag, setSelectedAlgomerchTag] =
    React.useState<AlgomerchTag>(AlgomerchTag.Cheapest);

  const expState = useExperiments();

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
    ) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const handleClickAlgomerchTag = (tagText: string) => {
    const allTags = Object.keys(AlgomerchTag);
    const selectedTag = allTags.find((tag) =>
      tagText.includes(mapAlgomerchTexts[tag]),
    );

    setSelectedAlgomerchTag(
      (selectedTag as AlgomerchTag) ?? AlgomerchTag.Cheapest,
    );
  };

  const expandedFareDetails = useMemo(() => {
    const fetchedAllFareDetails = fareTrips.length
      ? fareTrips.reduce(
          (hasFetchedFareTrip: boolean, fareTrip: any) =>
            hasFetchedFareTrip && !!tripDetailsById[fareTrip.trip],
          true,
        )
      : false;

    return fetchedAllFareDetails
      ? getSliceFareDetails({
          tripDetailsById,
          fareTrips,
        })
      : null;
  }, [fareTrips, tripDetailsById]);

  useEffect(() => {
    if (isFlightShopLoading) setFaresToShow([]);
  }, [isFlightShopLoading]);

  const fetchMoreData = useCallback(() => {
    setFaresToShow((prev) =>
      sortedFlights.slice(0, prev.length + SHOW_MORE_NUM),
    );
  }, [sortedFlights]);

  useEffect(() => {
    if (
      !isFlightShopLoading &&
      sortedFlights.length > 0 &&
      (isOutgoing || isReturn)
    ) {
      trackEvent({
        eventName: VIEWED_FLIGHT_LIST,
        properties: {
          ...viewedFlightListProperties,
        },
        encryptedProperties: [],
      });
    }
  }, [isFlightShopLoading, isOutgoing]);

  useEffect(() => {
    if (sortedFlights.length > 0) {
      if (!matchesMobile) {
        setFaresToShow(sortedFlights.slice(0, INITIAL_RESULT_SET_SIZE));
      } else {
        setFaresToShow(sortedFlights);
      }
    } else {
      setFaresToShow([]);
    }
  }, [sortedFlights]);

  useEffect(() => {
    // select flight if expandedFlight is set from url param but fareTrips isn't populated
    // ensures fareTrips (and by extension expandedFareDetails) is populated
    // ex. when navigating backwards via browser nav buttons
    if (expandedFlight && !fareTrips.length) {
      const flight = (faresToShow as IFlightListData[]).find(
        (fare) => fare.slice === expandedFlight,
      );
      if (flight) {
        isOutgoing
          ? handleFlightSelect(flight.fares.map((fare: any) => fare.example))
          : handleFlightSelect(
              flight.fares.map((fare: any) => ({
                trip: fare.tripId,
                fare: fare.id,
              })),
            );
      }
    }
  }, [fareTrips.length, expandedFlight, faresToShow]);

  useEffect(() => {
    // reset fare trips so we always populate it correctly
    // when navigating between outgoing/return flight lists
    setFareTrips([]);
  }, [isOutgoing]);

  // TODO: this should be replaced with actual filters
  const fareClassFilter: FareclassOptionFilter = {
    basic: false,
    standard: false,
    enhanced: false,
    premium: false,
    luxury: false,
  };

  const handleFlightSelect = (fareTrips: any) => {
    const uniqueTripIds: string[] = Array.from(
      new Set(fareTrips.map((fareTrip: any) => fareTrip.trip)),
    );
    uniqueTripIds.map((fareTrip: string) => {
      fetchPackagesTripDetails(history, fareTrip);
    });

    setFareTrips(fareTrips);
  };

  const getFareId = (fare: any) => fare.example?.fare || fare.id;

  const isFarePackageExclusiveRate = (fareId: string) =>
    flights.fares[fareId]?.isPackageExclusiveRate === true;

  const handleSliceSelect = (sliceId: string, flight: IFlightListData) => {
    if (sliceId === expandedFlight) {
      setExpandedFlight("");
    } else {
      setExpandedFlight(sliceId);
      isOutgoing
        ? handleFlightSelect(flight.fares.map((fare: any) => fare.example))
        : handleFlightSelect(
            flight.fares.map((fare: any) => ({
              trip: fare.tripId,
              fare: fare.id,
            })),
          );

      const slice = flights.slices[flight.slice];
      const airline = slice
        ? flights.airlines[slice.marketingAirline]
        : undefined;

      trackEvent({
        eventName: VIEWED_SLICE,
        properties: {
          ...viewedFlightListProperties,
          has_package_exclusive_rate: flight.fares.some(
            (fare: any) => isFarePackageExclusiveRate(getFareId(fare)),
          ),
          airline: airline?.displayName ?? slice?.marketingAirline,
          funnel: "packages",
        },
        encryptedProperties: [],
      });
    }
  };

  const handleFareSelect = (
    flight: any,
    fareId: string,
    _idx: number,
    _limit?: FiatPrice | null,
  ) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    if (flights && expandedFareDetails) {
      const tripId = expandedFareDetails?.id;

      if (isOutgoing) {
        const fareSliceId = flights.fares[fareId].outbound;
        const outgoingPackage = packagesByOutboundFareSlice[fareSliceId];

        setPackagesChosenOutgoingSlice(
          flight.slice,
          fareId,
          fareSliceId,
          tripId,
          flights.fareSlices[flights.fares[fareId].outbound].fareShelf.value,
          // whenever selecting a different departure flight, reset return ids
          true,
        );
        if (outgoingPackage.lodgingStayDates) {
          setHotelShopDateRange(outgoingPackage.lodgingStayDates);
        }

        const outgoingSlice = flights.slices[flight.slice];
        const outgoingAirline = outgoingSlice
          ? flights.airlines[outgoingSlice.marketingAirline]
          : undefined;

        trackEvent({
          eventName: SELECTED_FLIGHT,
          properties: {
            ...selectedOutgoingSliceProperties,
            recommended_flight_selected:
              outgoingRecommendedFlight?.slice == flight.slice,
            fare_is_package_exclusive_rate:
              isFarePackageExclusiveRate(fareId),
            airline: outgoingAirline?.displayName ?? outgoingSlice?.marketingAirline,
            funnel: "packages",
          },
          encryptedProperties: [],
        });

        const params = new URLSearchParams(history.location.search);
        params.delete("selectedFareId");
        params.delete("expandedFlight");
        params.delete("clickedFareId");
        params.set(
          "flightShopProgress",
          FlightShopStep.ChooseReturn.toString(),
        );
        history.push({ search: params.toString() });
      } else {
        const fareSliceId = flights.fares[fareId].return;
        const returnPackage = packagesByReturnFareSlice[fareSliceId];

        setPackagesChosenReturnSlice(
          tripId,
          flight.slice,
          fareId,
          fareSliceId,
          flights.fareSlices[flights.fares[fareId].return].fareShelf.value,
        );
        if (returnPackage.lodgingStayDates) {
          setHotelShopDateRange(returnPackage.lodgingStayDates);
        }

        if (matchesMobile || !isPackageCheckoutOptimizationExperiment) {
          goToReview({ history });
        } else {
          goToBook({ history });
        }

        const returnSlice = flights.slices[flight.slice];
        const returnAirline = returnSlice
          ? flights.airlines[returnSlice.marketingAirline]
          : undefined;

        trackEvent({
          eventName: SELECTED_FLIGHT,
          properties: {
            ...selectedReturnSliceProperties,
            recommended_flight_selected:
              returnRecommendedFlight?.slice == flight.slice,
            fare_is_package_exclusive_rate:
              isFarePackageExclusiveRate(fareId),
            airline: returnAirline?.displayName ?? returnSlice?.marketingAirline,
            funnel: "packages",
          },
          encryptedProperties: [],
        });
      }
    }
  };

  return (
    <Box className={clsx("packages-flight-list-root")}>
      {!isFlightShopLoading && !isFlightShopCallNotCalled && flights && (
        <>
          {(() => {
            switch (true) {
              case !!packagesByOutboundFareSlice &&
                faresToShow.length > 0 &&
                matchesMobile:
                return (
                  <MobileFlightList
                    flights={flights}
                    selectedFareId={selectedFareId}
                    expandedFlight={expandedFlight}
                    fareClassFilter={fareClassFilter}
                    maxPriceFilter={maxPriceFilter}
                    isOutgoing={isOutgoing}
                    packageNightCountBasedOnAvailability={
                      packageNightCountBasedOnAvailability
                    }
                    packageNightCountBasedOnOutboundFlight={
                      packageNightCountBasedOnOutboundFlight
                    }
                    packagesByOutboundFareSlice={packagesByOutboundFareSlice}
                    packagesByReturnFareSlice={packagesByReturnFareSlice}
                    departureDate={departureDate}
                    returnDate={returnDate}
                    hotelShopDateRange={hotelShopDateRange}
                    isTripDetailsLoading={!!isTripDetailsLoading}
                    airports={airports}
                    rewardsKey={rewardsKey}
                    isAgentPortal={isAgentPortal}
                    expandedFareDetails={expandedFareDetails}
                    handleClickAlgomerchTag={handleClickAlgomerchTag}
                    setOpenModal={setOpenModal}
                    handleSliceSelect={handleSliceSelect}
                    setClickedFareId={setClickedFareId}
                    setExpandedFlight={setExpandedFlight}
                    handleFareSelect={handleFareSelect}
                    faresToShow={faresToShow}
                    outgoingRecommendedFlight={outgoingRecommendedFlight}
                    returnRecommendedFlight={returnRecommendedFlight}
                    isXmodRedesign={isXmodRedesign}
                    selectedRewardsAccount={selectedRewardsAccount}
                    canEarnRewards={canEarnRewards}
                  />
                );

              case !!packagesByOutboundFareSlice &&
                faresToShow.length > 0 &&
                !matchesMobile:
                return (
                  <DesktopFlightList
                    flights={flights}
                    selectedFareId={selectedFareId}
                    expandedFlight={expandedFlight}
                    fareClassFilter={fareClassFilter}
                    maxPriceFilter={maxPriceFilter}
                    isOutgoing={isOutgoing}
                    packageNightCountBasedOnAvailability={
                      packageNightCountBasedOnAvailability
                    }
                    packageNightCountBasedOnOutboundFlight={
                      packageNightCountBasedOnOutboundFlight
                    }
                    packagesByOutboundFareSlice={packagesByOutboundFareSlice}
                    packagesByReturnFareSlice={packagesByReturnFareSlice}
                    departureDate={departureDate}
                    returnDate={returnDate}
                    hotelShopDateRange={hotelShopDateRange}
                    isTripDetailsLoading={!!isTripDetailsLoading}
                    airports={airports}
                    rewardsKey={rewardsKey}
                    isAgentPortal={isAgentPortal}
                    expandedFareDetails={expandedFareDetails}
                    handleClickAlgomerchTag={handleClickAlgomerchTag}
                    setOpenModal={setOpenModal}
                    handleSliceSelect={handleSliceSelect}
                    setClickedFareId={setClickedFareId}
                    setExpandedFlight={setExpandedFlight}
                    handleFareSelect={handleFareSelect}
                    faresToShow={faresToShow}
                    sortedFlights={sortedFlights}
                    outgoingRecommendedFlight={outgoingRecommendedFlight}
                    returnRecommendedFlight={returnRecommendedFlight}
                    fetchMoreData={fetchMoreData}
                    isXmodRedesign={isXmodRedesign}
                    selectedRewardsAccount={selectedRewardsAccount}
                    canEarnRewards={canEarnRewards}
                  />
                );
              case hasFlightShopCallFailed:
                trackEvent({
                  eventName: MODAL_ALERT,
                  properties: {
                    type: "packages_flight_list_error",
                    primary_button: constants.RELOAD,
                    screen: ModalScreens.FLIGHTS_SHOP,
                    category: ModalCategoryType.TROUBLE,
                    modal_subtitle: constants.ERROR_SUBTITLE,
                    modal_title: constants.ERROR_TITLE,
                    agent_title: constants.ERROR_TITLE,
                    agent_subtitle: constants.ERROR_TITLE,
                    funnel: "packages",
                    step: "flight_list",
                  },
                });

                return (
                  <Box className="no-results-container">
                    <NoResults
                      className="flight-list-no-results"
                      title={constants.ERROR_TITLE}
                      subtitle={constants.ERROR_SUBTITLE}
                    />
                    <button
                      onClick={() => window.location.reload()}
                      className="reload-button"
                    >
                      {constants.RELOAD}
                    </button>
                  </Box>
                );

              case hasAppliedNonFareclassFilter:
                return (
                  <Box className={clsx("no-results-container", "air-cx-v3")}>
                    <NoResults
                      className="flight-list-no-results"
                      title={FLIGHTS_NOT_FOUND_TITLE}
                      subtitle={FLIGHTS_NOT_FOUND_SUBTITLE}
                    />

                    <Box className="no-results-buttons-container">
                      <Button
                        onClick={resetFilters}
                        className={clsx("reset-filters-dates-button", "b2b")}
                      >
                        {CLEAR_FILTERS_CTA_TEXT}
                      </Button>
                    </Box>
                  </Box>
                );

              default:
                return null;
            }
          })()}
        </>
      )}

      <FlightAlgomerchModal
        selectedCategory={selectedAlgomerchTag}
        setSelectedCategory={setSelectedAlgomerchTag}
        openModal={openModal === ModalTypes.AlgomerchModal}
        onClose={() => setOpenModal(false)}
      />
    </Box>
  );
};
